import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import ClientEquipmentBidirecional from "../../components/ClientEquipmentBidirecional/ClientEquipmentBidirecional";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import SelectComponent from "../../components/SelectComponent";
import useValidationSchema from "../../components/Validations";
import { MaintenancePlanUtilities } from "../../helpers/MaintenancePlanUtilities";
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { EquipmentDTO } from "../../models/Equipment/EquipmentDTO";
import { CreateMaintenancePlanDTO } from "../../models/MaintenancePlan/CreateMaintenancePlanDTO";
import { MaintenancePlanService } from "../../services/MaintenancePlanService";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";

export type SelectItemsProps = {
    label: string;
    value: string;
};

export default function MaintenancePlanCreate() {
    // props para novo componente
    const [isActiveProjectModule, setIsActiveProjectModule] = useState<boolean>(false);
    const [projectSelected, setProjectSelected] = useState<SelectItemsProps | null>(null);
    const [clientSelected, setClientSelected] = useState<SelectItemsProps | null>(null);
    const [serialNumberSelected, setSerialNumberSelected] = useState<SelectItemsProps | null>(null);
    const [equipment, setEquipment] = useState<EquipmentDTO>({} as EquipmentDTO);
    const [isSearchEquipmentLoading, setIsSearchEquipmentLoading] = useState<boolean>(false);
    const [carPlateSelected, setCarPlateSelected] = useState<SelectItemsProps | null>(null);


    const intl = useIntl();
    const maintenancePlanService = new MaintenancePlanService();
    const [maintenancePlan, setMaintenancePlan] = useState<CreateMaintenancePlanDTO>({
        periodicityType: "Monthly"
    } as CreateMaintenancePlanDTO);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const navigate = useNavigate();

    const [locationType, setLocationType] = useState<string | null>(null);
    const locationTypeList = MaintenancePlanUtilities.getLocationTypeList();


    const [periodicityType, setPeriodicityType] = useState<string | null>("Monthly");
    const periodicityTypeList = MaintenancePlanUtilities.getPeriodicityTypeList(intl);


    const [periodicityRecurrence, setPeriodicityRecurrence] = useState<string | null>(null);
    const periodicityRecurrenceList = MaintenancePlanUtilities.getPeriodicityRecurrenceList();
    const [weeklyPeriodicityRecurrence, setWeeklyPeriodicityRecurrence] = useState<string | null>(null);
    const weeklyPeriodicityRecurrenceList = MaintenancePlanUtilities.getWeeklyPeriodicityRecurrenceList();

    const [periodicityWeekNumber, setPeriodicityWeekNumber] = useState<string | null>(null);
    const periodicityWeekNumberList = MaintenancePlanUtilities.getPeriodicityWeekNumberList(intl);

    const [periodicityDayOfWeek, setPeriodicityDayOfWeek] = useState<string | null>(null);
    const periodicityDayOfWeekList = MaintenancePlanUtilities.getPeriodicityDayOfWeekList(intl);

    const [applicant, setApplicant] = useState<string | null>(null);

    //#region Módulos ativos
    const { availableModules } = useAvailableModulesContext();
    const [isActiveCarPlate, setIsActiveCarPlate] = useState<boolean>(false);
    useEffect(() => {
        if (availableModules.includes("IsActiveCarPlate")) {
            setIsActiveCarPlate(true);
        }
    }, [availableModules]);
    //endregion

    // Validações (campo, tipo dados, mensagem (pt.json))
    const fields = MaintenancePlanUtilities.getValidationFields(periodicityType);
    const schemaClientValidation = useValidationSchema(fields);

    const CreateMaintenancePlan = async () => {
        setIsLoading(true);
        const createMaintenancePlan: CreateMaintenancePlanDTO = {
            name: maintenancePlan.name ?? null,
            clientErpId: equipment?.client?.erpId ?? clientSelected?.value ?? null,
            applicantEmail: maintenancePlan?.applicantEmail,
            equipmentErpId: serialNumberSelected?.value == null ? null : equipment?.erpId ?? null,
            startDate: maintenancePlan.startDate,
            locationType: maintenancePlan.locationType ?? null,
            notes: maintenancePlan.notes ?? null,
            expectedHours: Utilities.HandleEmptyValues(maintenancePlan?.expectedHours) ?? null,

            periodicityType: maintenancePlan?.periodicityType,
            periodicityRecurrence: maintenancePlan?.periodicityRecurrence,
            periodicityWeekNumber: maintenancePlan?.periodicityWeekNumber,
            periodicityDayOfWeek: maintenancePlan?.periodicityDayOfWeek
        };

        const { error } = schemaClientValidation.validate(createMaintenancePlan);
        if (error) {
            Toast.Show("error", error.details.map(detail => detail.message).join(', '));
            setIsLoading(false);
            return;
        }
        const response = await maintenancePlanService.Create(createMaintenancePlan);

        if (response.success !== true) {
            Toast.Show("error", response.message);
            setIsLoading(false);
            return;
        }
        Toast.Show("success", response.message);
        setIsLoading(false);

        navigate(`${Routes.maintenancePlans.details}/${response.obj}`);
    };



    useEffect(() => {
        if (clientSelected === null) {
            setApplicant(null);
            setMaintenancePlan({ ...maintenancePlan, applicantEmail: null });
        }
    }, [clientSelected]);


    //#region codigo relevante para criar novo plano através de navegação do cliente
    const location = useLocation();
    useEffect(() => {
        if (location.state) {
            const { clientErpId, clientName, client, equipment } = location.state;

            if (clientErpId && clientName) {
                setClientSelected({ value: clientErpId, label: clientName });
            }

            if (client && equipment) {
                setClientSelected({ value: client.erpId, label: client.name });
                setCarPlateSelected({ value: equipment.erpId, label: equipment.carPlate });
                setSerialNumberSelected({ value: equipment.erpId, label: equipment.serialNumber });
                setEquipment({
                    ...equipment!,
                    brand: equipment.brand,
                    model: equipment.model,
                });
            }
        }
    }, [location.state]);
    //#endregion

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <PageTitle
                        title={intl.formatMessage({ id: 'Screens.MaintenancePlans.Create.Title' })}
                        allowGoBack={true}
                    />
                </div>
                <div className="pageBody">
                    <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-start", alignContent: "flex-start", display: "flex", flexDirection: "row" }}>
                        <Col xl={6} lg={12} md={12} sm={12} xs={12}>

                            <ClientEquipmentBidirecional
                                isActiveProjectModule={isActiveProjectModule}
                                projectSelected={projectSelected}
                                clientSelected={clientSelected}
                                setClientSelected={setClientSelected}
                                serialNumberSelected={serialNumberSelected}
                                setSerialNumberSelected={setSerialNumberSelected}
                                equipment={equipment}
                                setEquipment={setEquipment}
                                isSearchEquipmentLoading={isSearchEquipmentLoading}
                                setIsSearchEquipmentLoading={setIsSearchEquipmentLoading}
                                isActiveSearchByCarPlate={isActiveCarPlate}
                                carPlateSelected={carPlateSelected}
                                setCarPlateSelected={setCarPlateSelected}
                                disableSoftwareFields={true}
                            />

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <SelectComponent
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.Applicant" })}
                                    selectedItemValue={applicant}
                                    items={MaintenancePlanUtilities.GetClientApplicants}
                                    loadArgs={[clientSelected?.value]}
                                    onChange={(applicant: string) => {
                                        setApplicant(applicant);
                                        setMaintenancePlan({ ...maintenancePlan!, applicantEmail: applicant });
                                    }}
                                />
                            </Row>

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <InputComponent
                                    xl={8}
                                    lg={8}
                                    md={8}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type={"text"}
                                    value={maintenancePlan.name}
                                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.MaintenancePlanName" })}
                                    readOnly={false}
                                    onChange={(name: string) => {
                                        setMaintenancePlan({ ...maintenancePlan!, name: name });
                                    }}
                                />
                            </Row>

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <InputComponent
                                    xl={2}
                                    lg={2}
                                    md={2}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type={"date"}
                                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.StartDate" })}
                                    readOnly={false}
                                    onChange={(t: Date) => {
                                        setMaintenancePlan({ ...maintenancePlan!, startDate: t });
                                    }}
                                />
                            </Row>

                            {periodicityType === "Monthly" && (
                                <Row
                                    style={{
                                        margin: "0px",
                                        padding: "0px",
                                        alignItems: "flex-end",
                                        alignContent: "flex-end",
                                    }}
                                >
                                    <SelectComponent
                                        xl={3}
                                        lg={3}
                                        md={3}
                                        sm={12}
                                        xs={12}
                                        style={{ width: "100%" }}
                                        label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.PeriodicityType" })}
                                        selectedItemValue={periodicityType}
                                        items={periodicityTypeList ?? []}
                                        onChange={(periodicityType: string) => {
                                            setPeriodicityType(periodicityType);
                                            setMaintenancePlan({ ...maintenancePlan!, periodicityType: periodicityType });
                                        }}
                                    />

                                    <SelectComponent
                                        xl={3}
                                        lg={3}
                                        md={3}
                                        sm={12}
                                        xs={12}
                                        style={{ width: "100%" }}
                                        label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.PeriodicityRecurrence" })}
                                        selectedItemValue={periodicityRecurrence}
                                        items={periodicityRecurrenceList ?? []}
                                        onChange={(periodicityRecurrence: string) => {
                                            setPeriodicityRecurrence(periodicityRecurrence);
                                            setMaintenancePlan({ ...maintenancePlan!, periodicityRecurrence: Number(periodicityRecurrence) });
                                        }}
                                    />

                                    <SelectComponent
                                        xl={3}
                                        lg={3}
                                        md={3}
                                        sm={12}
                                        xs={12}
                                        style={{ width: "100%" }}
                                        label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.PeriodicityWeekNumber" })}
                                        selectedItemValue={periodicityWeekNumber}
                                        items={periodicityWeekNumberList ?? []}
                                        onChange={(periodicityWeekNumber: string) => {
                                            setPeriodicityWeekNumber(periodicityWeekNumber);
                                            setMaintenancePlan({ ...maintenancePlan!, periodicityWeekNumber: periodicityWeekNumber });
                                        }}
                                    />

                                    <SelectComponent
                                        xl={3}
                                        lg={3}
                                        md={3}
                                        sm={12}
                                        xs={12}
                                        style={{ width: "100%" }}
                                        label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.PeriodicityDayOfWeek" })}
                                        selectedItemValue={periodicityDayOfWeek}
                                        items={periodicityDayOfWeekList ?? []}
                                        onChange={(periodicityDayOfWeek: string) => {
                                            setPeriodicityDayOfWeek(periodicityDayOfWeek);
                                            setMaintenancePlan({ ...maintenancePlan!, periodicityDayOfWeek: periodicityDayOfWeek });
                                        }}
                                    />
                                </Row>
                            )}

                            {periodicityType === "Weekly" && (
                                <Row
                                    style={{
                                        margin: "0px",
                                        padding: "0px",
                                        alignItems: "flex-end",
                                        alignContent: "flex-end",
                                    }}
                                >
                                    <SelectComponent
                                        xl={3}
                                        lg={3}
                                        md={3}
                                        sm={12}
                                        xs={12}
                                        style={{ width: "100%" }}
                                        label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.PeriodicityType" })}
                                        selectedItemValue={periodicityType}
                                        items={periodicityTypeList ?? []}
                                        onChange={(periodicityType: string) => {
                                            setPeriodicityType(periodicityType);
                                            setMaintenancePlan({ ...maintenancePlan!, periodicityType: periodicityType });
                                        }}
                                    />

                                    <SelectComponent
                                        xl={3}
                                        lg={3}
                                        md={3}
                                        sm={12}
                                        xs={12}
                                        style={{ width: "100%" }}
                                        label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.PeriodicityRecurrence" })}
                                        selectedItemValue={weeklyPeriodicityRecurrence}
                                        items={weeklyPeriodicityRecurrenceList ?? []}
                                        onChange={(weeklyPeriodicityRecurrence: string) => {
                                            setWeeklyPeriodicityRecurrence(weeklyPeriodicityRecurrence);
                                            setMaintenancePlan({ ...maintenancePlan!, periodicityRecurrence: Number(weeklyPeriodicityRecurrence) });
                                        }}
                                    />

                                    <SelectComponent
                                        xl={3}
                                        lg={3}
                                        md={3}
                                        sm={12}
                                        xs={12}
                                        style={{ width: "100%" }}
                                        label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.PeriodicityDayOfWeek" })}
                                        selectedItemValue={periodicityDayOfWeek}
                                        items={periodicityDayOfWeekList ?? []}
                                        onChange={(periodicityDayOfWeek: string) => {
                                            setPeriodicityDayOfWeek(periodicityDayOfWeek);
                                            setMaintenancePlan({ ...maintenancePlan!, periodicityDayOfWeek: periodicityDayOfWeek });
                                        }}
                                    />
                                </Row>
                            )}

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <SelectComponent
                                    xl={3}
                                    lg={3}
                                    md={3}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.LocationType" })}
                                    selectedItemValue={locationType}
                                    items={locationTypeList ?? []}
                                    onChange={(t: string) => {
                                        setLocationType(t);
                                        setMaintenancePlan({ ...maintenancePlan!, locationType: t });
                                    }}
                                />
                                <InputComponent
                                    xl={2}
                                    lg={2}
                                    md={2}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type={"number"}
                                    value={maintenancePlan.expectedHours}
                                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.ExpectedHours" })}
                                    readOnly={false}
                                    onChange={(expectedHours: number) => {
                                        setMaintenancePlan({ ...maintenancePlan!, expectedHours: expectedHours });
                                    }}
                                />
                            </Row>

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <InputComponent
                                    xl={12}
                                    lg={12}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%", height: "120px" }}
                                    type={"textArea"}
                                    value={maintenancePlan.notes}
                                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.Notes" })}
                                    readOnly={false}
                                    onChange={(notes: string) => {
                                        setMaintenancePlan({ ...maintenancePlan!, notes: notes });
                                    }}
                                />
                            </Row>

                            <Row style={{ margin: "0px", marginTop: "10px", padding: "0px" }}>
                                <Col xl={1} lg={1} md={1} sm={1} xs={1}>
                                    <ButtonComponent
                                        disabled={isLoading}
                                        loading={isLoading}
                                        text={intl.formatMessage({ id: "Screens.MaintenancePlans.Create.Button" })}
                                        onClick={CreateMaintenancePlan}
                                    />
                                </Col>
                            </Row>

                        </Col>
                    </Row>
                </div>
            </div>
        </Layout >
    );
}
